import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { ETAPA, ORDEN_ETAPAS, esFlujoExport, ZONAS, ESTADO_ETAPA, direccionTexto, horarioTexto } from "@/lib/etapas";
import { ESTADOS_US } from "@/lib/constants";
import { formatFechaHoraZona, formatFechaHoraPared, formatHora } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ChevronDown, Check, TriangleAlert, Plus, X } from "lucide-react";
import { t } from "@/lib/i18n";

const OTRO = "__otro__";
const SIN = "__sin__";

function Campo({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[12.5px] text-ink-2">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Fix P (2026-10-13): las etapas de la carga como tarjetas. Sirve para «Nueva carga» y para el expediente.
// `etapas` es lo que dispatch edita (ver lib/etapas.js); `servidor` y `carga` (solo en el expediente) traen lo que reportó el chofer.
export function EtapasEditor({ etapas, onChange, servidor, carga, choferes, ubicaciones, abiertasInicial = "todas", soloLectura = false }) {
  const [abiertas, setAbiertas] = useState(() => (
    abiertasInicial === "todas" ? ORDEN_ETAPAS.filter((c) => etapas.find((e) => e.clave === c)?.activa) : [carga?.etapa_actual].filter(Boolean)
  ));
  const [asignarKey, setAsignarKey] = useState(0);
  const serv = (clave) => (servidor || []).find((s) => s.clave === clave);
  const nombreChofer = (id, clave) => choferes.find((c) => c.id === id)?.nombre || serv(clave)?.chofer_nombre || "";

  const cambiar = (clave, patch) => {
    const i = etapas.findIndex((e) => e.clave === clave);
    const antes = etapas[i];
    let sig = etapas.map((e) => (e.clave === clave ? { ...e, ...patch } : e));
    // Si cambia el día de una etapa, las siguientes que tenían el mismo día lo siguen (lo normal es hacer todo el mismo día).
    if (patch.fecha && patch.fecha !== antes.fecha) {
      sig = sig.map((e, j) => (j > i && e.activa && e.fecha === antes.fecha && !serv(e.clave)?.salida_at ? { ...e, fecha: patch.fecha } : e));
    }
    onChange(sig);
  };

  const alternar = (clave) => setAbiertas((p) => (p.includes(clave) ? p.filter((c) => c !== clave) : [...p, clave]));

  const asignarTodas = (id) => {
    onChange(etapas.map((e) => (e.activa && !serv(e.clave)?.salida_at ? { ...e, chofer_id: id === SIN ? "" : id } : e)));
    setAsignarKey((k) => k + 1);
  };

  const yard = etapas.find((e) => e.clave === "yard_stop");
  const yardServ = serv("yard_stop");
  const alternarYarda = () => {
    const activar = !yard.activa;
    cambiar("yard_stop", { activa: activar });
    if (activar) setAbiertas((p) => (p.includes("yard_stop") ? p : [...p, "yard_stop"]));
  };

  const activas = ORDEN_ETAPAS.filter((c) => etapas.find((e) => e.clave === c)?.activa);

  return (
    <div className="space-y-3" data-testid="etapas-editor">
      {!soloLectura && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[13.5px] text-ink-3">{t("Cada etapa lleva su propio chofer. Toca una etapa para ver y editar sus datos.")}</p>
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] text-ink-2">{t("Asignar todas a")}</span>
            <Select key={asignarKey} onValueChange={asignarTodas}>
              <SelectTrigger data-testid="asignar-todas-select" className="h-9 w-48 bg-white"><SelectValue placeholder={t("Elegir chofer")} /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value={SIN}>{t("Sin asignar")}</SelectItem>
                {choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {activas.map((clave, idx) => (
        <EtapaCard
          key={clave}
          e={etapas.find((x) => x.clave === clave)}
          num={idx + 1}
          serv={serv(clave)}
          carga={carga}
          abierta={abiertas.includes(clave)}
          onToggle={() => alternar(clave)}
          onCambiar={(patch) => cambiar(clave, patch)}
          choferes={choferes}
          nombreChofer={nombreChofer(etapas.find((x) => x.clave === clave).chofer_id, clave)}
          ubicaciones={ubicaciones}
          soloLectura={soloLectura}
        />
      ))}

      {yard && !esFlujoExport(etapas) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-line-strong bg-surface px-4 py-3" data-testid="yarda-opcional">
          <p className="text-[14px] text-ink-2" data-testid="yarda-texto">
            {yard.activa ? t("Parada en yarda activada · el chofer carga el espacio al llegar") : t("Parada en yarda opcional · Desactivada para recorrido directo")}
          </p>
          {!soloLectura && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="yarda-alternar"
              disabled={yard.activa && !!yardServ?.llegada_at}
              onClick={alternarYarda}
            >
              {yard.activa ? <><X className="h-3.5 w-3.5" />{t(" Quitar parada en yarda")}</> : <><Plus className="h-3.5 w-3.5" />{t(" Activar parada en yarda")}</>}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function EtapaCard({ e, num, serv, carga, abierta, onToggle, onCambiar, choferes, nombreChofer, ubicaciones, soloLectura }) {
  const cfg = ETAPA[e.clave];
  const dir = direccionTexto(e);
  const hor = horarioTexto(e);
  const catalogo = ubicaciones.filter((u) => cfg.ubic.includes(u.tipo) && (u.activo || u.id === e.ubicacion_id));
  const completada = !!serv?.salida_at;
  const est = serv ? ESTADO_ETAPA[serv.estado_etapa] : null;
  const tt = (campo) => `etapa-${e.clave}-${campo}`;

  const elegirUbicacion = (id) => {
    if (id === OTRO) { onCambiar({ ubicacion_id: "" }); return; }
    const u = ubicaciones.find((x) => x.id === id);
    if (!u) return;
    const soloTexto = !u.calle && !u.ciudad && !u.estado_us && !u.zip;
    onCambiar({
      ubicacion_id: u.id, lugar: u.nombre, calle: soloTexto ? (u.direccion || "") : (u.calle || ""),
      ciudad: u.ciudad || "", estado_us: u.estado_us || "", zip: u.zip || "", es_yarda_propia: !!u.yarda_propia,
    });
  };

  return (
    <section className="bg-surface border border-line rounded-xl overflow-hidden" data-testid={`etapa-${e.clave}`}>
      <button
        type="button"
        data-testid={tt("cabecera")}
        onClick={onToggle}
        aria-expanded={abierta}
        className="w-full text-left flex items-start gap-3 px-4 lg:px-5 py-4 hover:bg-surface-2 transition-colors duration-150"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-display font-bold text-[20px] leading-tight text-navy-2">{num} · {t(e.nombre)}</span>
            {est && (
              <span data-testid={tt("estado")} className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-[2px] text-[12.5px] font-semibold", est.cls)}>
                {serv.estado_etapa === "completada" && <Check className="h-3.5 w-3.5" />}{est.label}
              </span>
            )}
          </span>
          <span className="block text-[15px] text-ink mt-1">{e.lugar || <span className="text-ink-3">{t("Sin lugar")}</span>} · {nombreChofer || <span className="text-ink-3">{t("Sin chofer")}</span>}</span>
          {dir && <span className="block text-[13.5px] text-ink-2 mt-0.5">{dir}</span>}
          {hor && <span className="block text-[13px] text-ink-3 mt-0.5">{hor}</span>}
        </span>
        <ChevronDown className={cn("h-5 w-5 text-ink-3 mt-1 shrink-0 transition-transform duration-150", abierta && "rotate-180")} />
      </button>

      {abierta && (
        <fieldset disabled={soloLectura} className="px-4 lg:px-5 pb-5 pt-4 border-t border-line space-y-4 min-w-0" data-testid={tt("cuerpo")}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Campo label={t("Nombre de la etapa")}>
              <Input data-testid={tt("nombre")} value={e.nombre} onChange={(ev) => onCambiar({ nombre: ev.target.value })} className="h-10" />
            </Campo>
            <Campo label={completada ? t("Chofer (la etapa ya está completada)") : t("Chofer de esta etapa")}>
              <Select value={e.chofer_id || SIN} onValueChange={(v) => onCambiar({ chofer_id: v === SIN ? "" : v })} disabled={completada}>
                <SelectTrigger data-testid={tt("chofer")} className="h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value={SIN}>{t("Sin asignar")}</SelectItem>
                  {choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  {e.chofer_id && !choferes.some((c) => c.id === e.chofer_id) && <SelectItem value={e.chofer_id}>{nombreChofer || t("Chofer")}</SelectItem>}
                </SelectContent>
              </Select>
            </Campo>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {cfg.ubic.length > 0 && (
              <Campo label={t("{lugar} (catálogo)", { lugar: cfg.lugar })}>
                <Select value={e.ubicacion_id || OTRO} onValueChange={elegirUbicacion}>
                  <SelectTrigger data-testid={tt("ubicacion")} className="h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value={OTRO}>{t("Otro lugar (escribirlo)")}</SelectItem>
                    {catalogo.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Campo>
            )}
            <Campo label={cfg.ubic.length > 0 ? t("Lugar") : cfg.lugar + (cfg.lugarObligatorio ? " *" : "")} className={cfg.ubic.length > 0 ? "" : "sm:col-span-2"}>
              <Input data-testid={tt("lugar")} value={e.lugar} onChange={(ev) => onCambiar({ lugar: ev.target.value })} className="h-10" />
            </Campo>
          </div>

          <div className="grid gap-3 sm:grid-cols-[2fr_1.2fr_.8fr_.8fr]">
            <Campo label={t("Calle y número")}><Input data-testid={tt("calle")} value={e.calle} onChange={(ev) => onCambiar({ calle: ev.target.value })} className="h-10" /></Campo>
            <Campo label={t("Ciudad")}><Input data-testid={tt("ciudad")} value={e.ciudad} onChange={(ev) => onCambiar({ ciudad: ev.target.value })} className="h-10" /></Campo>
            <Campo label={t("State")}>
              <Select value={e.estado_us || SIN} onValueChange={(v) => onCambiar({ estado_us: v === SIN ? "" : v })}>
                <SelectTrigger data-testid={tt("estado-us")} className="h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  <SelectItem value={SIN}>—</SelectItem>
                  {ESTADOS_US.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Campo>
            <Campo label={t("ZIP")}><Input data-testid={tt("zip")} value={e.zip} onChange={(ev) => onCambiar({ zip: ev.target.value })} inputMode="numeric" maxLength={10} className="h-10" /></Campo>
          </div>

          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            <Campo label={e.clave === "rail_pickup" ? t("Fecha *") : t("Fecha")}><Input data-testid={tt("fecha")} type="date" value={e.fecha} onChange={(ev) => onCambiar({ fecha: ev.target.value })} className="h-10" /></Campo>
            <Campo label={t("Desde")}><Input data-testid={tt("hora-desde")} type="time" value={e.hora_desde} onChange={(ev) => onCambiar({ hora_desde: ev.target.value })} className="h-10" /></Campo>
            <Campo label={t("Hasta")}><Input data-testid={tt("hora-hasta")} type="time" value={e.hora_hasta} onChange={(ev) => onCambiar({ hora_hasta: ev.target.value })} className="h-10" /></Campo>
            <Campo label={t("Días (nota)")}><Input data-testid={tt("dias")} value={e.dias} onChange={(ev) => onCambiar({ dias: ev.target.value })} placeholder={t("Ej.: lun–vie")} className="h-10" /></Campo>
          </div>
          {e.clave === "delivery" && (
            <p className="text-[12.5px] text-ink-3 -mt-2" data-testid="etapa-delivery-cita-ayuda">{t("Cita abierta: Desde y Hasta de la ventana (ej. 9:00 a 16:00). Cita fija: la misma hora en los dos.")}</p>
          )}

          {e.clave === "delivery" && (
            <div className="grid gap-3 grid-cols-2" data-testid="delivery-extra">
              <Campo label={t("Zona horaria del receiver")}>
                <Select value={e.zona_horaria} onValueChange={(v) => onCambiar({ zona_horaria: v })}>
                  <SelectTrigger data-testid={tt("zona")} className="h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(ZONAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Campo>
              <Campo label={t("Tipo de descarga")}>
                <div className="grid grid-cols-2 gap-1" role="group" aria-label={t("Tipo de descarga")}>
                  {[["live_unload", t("Live unload")], ["drop", t("Drop")]].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={tt(`tipo-${k}`)}
                      aria-pressed={e.tipo_descarga === k}
                      onClick={() => onCambiar({ tipo_descarga: k })}
                      className={cn("h-10 rounded-md border text-[13.5px] font-semibold transition-colors duration-150", e.tipo_descarga === k ? "bg-navy text-white border-navy" : "bg-white border-line-strong text-ink hover:bg-surface-2")}
                    >{l}</button>
                  ))}
                </div>
              </Campo>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label={t("Referencia")}><Input data-testid={tt("referencia")} value={e.referencia} onChange={(ev) => onCambiar({ referencia: ev.target.value })} placeholder={cfg.ref} className="h-10" /></Campo>
            {!["rail_pickup", "delivery"].includes(e.clave) && (
              <label className="flex items-center gap-2 text-sm text-ink-2 sm:mt-6">
                <input data-testid={tt("yarda-propia")} type="checkbox" checked={!!e.es_yarda_propia} onChange={(ev) => onCambiar({ es_yarda_propia: ev.target.checked })} className="h-4 w-4" />{t(" Es una yarda propia (Leona's yard)")}</label>
            )}
          </div>
          <Campo label={t("Instrucciones para el chofer")}>
            <Textarea data-testid={tt("instrucciones")} value={e.instrucciones} onChange={(ev) => onCambiar({ instrucciones: ev.target.value })} rows={2} />
          </Campo>

          {serv && carga && <ReportadoChofer e={serv} carga={carga} />}
        </fieldset>
      )}
    </section>
  );
}

// Recuadro verde: lo que mandó el chofer desde su app. Nadie lo edita en el panel.
function ReportadoChofer({ e, carga }) {
  const z = e.zona_horaria;
  const hora = (iso) => (iso ? formatFechaHoraZona(iso, z) : t("Pendiente"));
  const si = (v) => (v ? t("Sí") : t("No"));
  const doc = (carga.docs || []).find((d) => d.etapa === e.clave);
  const eta = carga.eta_vigente;
  const etaTexto = eta ? formatFechaHoraPared(eta.eta_fecha, eta.eta_hora, eta.zona_horaria) : t("Pendiente");
  const lineas = [[t("Llegada"), hora(e.llegada_at)], [t("Salida"), hora(e.salida_at)]];

  if (e.clave === "pick_chassis") lineas.push([t("Chassis reportado"), e.chassis_reportado || t("Pendiente")]);
  if (e.clave === "rail_pickup") {
    lineas.push([t("Contenedor reportado"), e.contenedor_reportado ? `${e.contenedor_reportado} · ${e.contenedor_coincide === false ? t("No coincide con el asignado ({numero_contenedor})", { numero_contenedor: carga.numero_contenedor }) : t("Coincide")}` : t("Pendiente"), e.contenedor_coincide === false]);
    lineas.push([t("Chassis reportado"), e.chassis_reportado || t("Pendiente")]);
    lineas.push([t("Delivery ETA"), etaTexto]);
  }
  if (e.clave === "empty_pickup") lineas.push([t("Contenedor reportado"), e.contenedor_reportado || t("Pendiente")]);
  if (e.clave === "yard_stop") lineas.push([t("Espacio en la yarda"), e.espacio || t("Pendiente")]);
  if (e.clave === "delivery") {
    lineas.push([t("Delivery ETA vigente"), etaTexto]);
    lineas.push([t("Descarga terminada"), si(e.descarga_terminada)]);
  }
  if (e.clave === "return_empty") lineas.push([t("Vacío devuelto"), si(e.vacio_devuelto)]);
  if (e.clave === "return_chassis") lineas.push([t("Chassis devuelto"), si(e.chassis_devuelto)]);
  if (doc) {
    const v = doc.estado === "recibido" ? t("Recibido ({total})", { total: doc.total }) : doc.requerido ? t("Pendiente (requerido)") : t("Opcional · sin recibir");
    lineas.push([doc.nombre, v, doc.estado === "pendiente"]);
  }

  return (
    <div className="rounded-xl border border-ok-line bg-ok-soft p-4" data-testid={`reportado-${e.clave}`}>
      <h4 className="text-[12px] font-bold tracking-[.08em] uppercase text-ok">{t("App del chofer · actualizaciones recibidas")}</h4>
      <ul className="mt-2.5 space-y-1 text-[14.5px] text-ok-ink">
        {lineas.map(([k, v, alerta]) => (
          <li key={k} data-testid={`reportado-${e.clave}-${k}`} className={cn(alerta && "font-semibold text-danger")}>
            {alerta && <TriangleAlert className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}{k}: {v}
          </li>
        ))}
      </ul>
      {e.clave === "delivery" && (carga.eta_historial || []).length > 0 && (
        <details className="mt-3 text-[13.5px] text-ok-ink" data-testid="eta-historial">
          <summary className="cursor-pointer font-semibold">{t("Historial de ETA ({length})", { length: carga.eta_historial.length })}</summary>
          <ul className="mt-1.5 space-y-0.5">
            {[...carga.eta_historial].reverse().map((h, i) => (
              <li key={i}>{t("{v0} · cargada a las {v1}{v2}", { v0: formatFechaHoraPared(h.eta_fecha, h.eta_hora, h.zona_horaria), v1: formatHora(h.cargada_at), v2: h.chofer_nombre ? t(" por {v0}", { v0: h.chofer_nombre }) : "" })}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
